import { useCallback, useEffect, useRef, useState } from 'react'
import {
  SyncChangeSchema,
  type SyncChangeData,
  type TripItemInputData,
  type TripItemPatch,
  type TripPatch,
  type TripStatus,
  type TripWithItems,
} from '@sp/shared'
import { api } from './api.ts'
import { HttpError } from './http.ts'
import {
  applyQueue,
  forgetTrip,
  loadCachedTrip,
  loadQueue,
  loadQueuedStatus,
  saveCachedTrip,
  saveQueue,
  saveQueuedStatus,
  tripsWithOutbox,
} from './outbox.ts'
import { newId } from './storage.ts'
import { errorMessage, invalidateResources, updateCachedResource } from './useResource.ts'

/** Jak casto se stahuji zmeny z druheho telefonu, kdyz nakupujete oba. */
const POLL_MS = 15_000
/** Server prijme nejvys 200 zmen najednou; posila se po mensich davkach. */
const BATCH = 100
/** Jak casto se na pozadi zkousi odeslat fronty nakupu, ktere nejsou otevrene. */
const BACKGROUND_MS = 30_000

/**
 * Zamek na urovni modulu, ne komponenty: planovani, vyber ze sablony
 * i nakupovani jsou ruzne obrazovky nad stejnou frontou (a odesilani na
 * pozadi taky). Dve soubezna odeslani teze fronty by `slice(batch.length)` rozbila.
 */
const flushing = new Set<string>()

/** Stav nakupu se zmenil mimo jeho obrazovku (kontextove menu). */
const OUTBOX_EVENT = 'sp:fronta-nakupu'

/** Zmena polozek, kterou server nikdy neprijme (400) — zahodila se. */
class RejectedBatch extends Error {}

export interface TripSync {
  trip: TripWithItems | null
  /** Neodeslane zmeny (polozky i stav nakupu). */
  pending: number
  offline: boolean
  error: string | null
  /** Nakup neexistuje nebo k nemu uzivatel nema pristup. */
  missing: boolean
  enqueue(changes: SyncChangeData[]): void
  change(itemId: string, patch: TripItemPatch): void
  add(items: Omit<TripItemInputData, 'id'>[]): void
  remove(itemId: string): void
  /** Stav nakupu (Nakupuju, Hotovo, zpet mezi rozpracovane) — funguje i bez signalu, odejde s frontou. */
  setStatus(status: TripStatus): void
  /** Ostatni zmeny nakupu samotneho (nazev, datum, trasa) jdou jen online. */
  updateTrip(patch: TripPatch): Promise<void>
  deleteTrip(): Promise<void>
  flush(): Promise<void>
}

/** Nakup s nactenou frontou a cekajicim stavem — to, co vidi obrazovka. */
function withOutbox(base: TripWithItems, userId: string): TripWithItems {
  const trip = applyQueue(base, loadQueue(base.id), userId)
  const status = loadQueuedStatus(base.id)
  return status && status !== trip.status ? { ...trip, status } : trip
}

/** Seznam nakupu ve skupine ukaze novy stav hned, i bez signalu. */
function patchTripList(groupId: string, tripId: string, status: TripStatus): void {
  updateCachedResource(`sp:nakupy:${groupId}`, (prev) =>
    Array.isArray(prev)
      ? prev.map((t: { id?: string }) =>
          t.id === tripId
            ? { ...t, status, finishedAt: status === 'done' ? new Date().toISOString() : null }
            : t,
        )
      : prev,
  )
}

/**
 * Odesle frontu nakupu: nejdriv polozky, pak stav (Hotovo az po vsech
 * odskrtnutich). Vraci cerstvy stav ze serveru, ulozeny i v cache.
 */
async function sendTrip(tripId: string): Promise<TripWithItems> {
  let latest: TripWithItems | null = null
  for (;;) {
    const queue = loadQueue(tripId)
    if (queue.length === 0) break
    const batch = queue.slice(0, BATCH)
    try {
      const res = await api.trips.sync(tripId, batch)
      // Zmeny pridane behem requestu zustavaji ve fronte a znovu se
      // aplikuji na cerstva data, jinak by je odpoved serveru prepsala.
      saveQueue(tripId, loadQueue(tripId).slice(batch.length))
      latest = { ...res.trip, items: res.items }
      saveCachedTrip(latest)
    } catch (err) {
      // 400 = davku server nikdy neprijme. Nechat ji ve fronte by
      // zablokovalo vsechny dalsi zmeny navzdy.
      if (err instanceof HttpError && err.status === 400) {
        saveQueue(tripId, loadQueue(tripId).slice(batch.length))
        throw new RejectedBatch('Část změn server odmítl a nebyla uložena.')
      }
      throw err
    }
  }
  const status = loadQueuedStatus(tripId)
  if (status) {
    try {
      const updated = await api.trips.update(tripId, { status })
      saveQueuedStatus(tripId, null)
      if (latest) {
        latest = { ...latest, ...updated }
        saveCachedTrip(latest)
      }
      invalidateResources('sp:nakupy:')
    } catch (err) {
      if (err instanceof HttpError && err.status === 400) {
        saveQueuedStatus(tripId, null)
        throw new RejectedBatch('Stav nákupu server odmítl.')
      }
      throw err
    }
  }
  if (!latest) {
    latest = await api.trips.get(tripId)
    saveCachedTrip(latest)
  }
  return latest
}

/** Odeslani jednoho nakupu mimo jeho obrazovku; chyby zkusi dalsi kolo. */
async function sendInBackground(tripId: string): Promise<void> {
  if (flushing.has(tripId)) return
  flushing.add(tripId)
  try {
    await sendTrip(tripId)
  } catch (err) {
    if (err instanceof HttpError && err.status === 404) forgetTrip(tripId)
  } finally {
    flushing.delete(tripId)
  }
}

/**
 * Stav nakupu ze seznamu nebo kontextoveho menu (bez otevrene obrazovky
 * nakupu) — stejna fronta, takze funguje i bez signalu.
 */
export function queueTripStatus(trip: { id: string; groupId: string; status: TripStatus }, status: TripStatus): void {
  saveQueuedStatus(trip.id, trip.status === status ? null : status)
  patchTripList(trip.groupId, trip.id, status)
  // Otevrena obrazovka teze nakupu se hned prekresli.
  window.dispatchEvent(new CustomEvent(OUTBOX_EVENT, { detail: trip.id }))
  void sendInBackground(trip.id)
}

/**
 * Odesilani front nakupu, ktere zrovna nejsou otevrene (treba „Hotovo" bez
 * signalu a pak zpet na seznam). Bezi v AppLayout.
 */
export function useBackgroundOutbox(): void {
  useEffect(() => {
    let live = true
    const run = async () => {
      if (!navigator.onLine) return
      for (const tripId of tripsWithOutbox()) {
        if (!live) return
        await sendInBackground(tripId)
      }
    }
    void run()
    const timer = setInterval(() => void run(), BACKGROUND_MS)
    const onOnline = () => void run()
    window.addEventListener('online', onOnline)
    return () => {
      live = false
      clearInterval(timer)
      window.removeEventListener('online', onOnline)
    }
  }, [])
}

export function useTripSync(tripId: string, userId: string): TripSync {
  const pendingCount = useCallback(
    () => loadQueue(tripId).length + (loadQueuedStatus(tripId) ? 1 : 0),
    [tripId],
  )
  const [trip, setTrip] = useState<TripWithItems | null>(() => {
    const cached = loadCachedTrip(tripId)
    return cached ? withOutbox(cached, userId) : null
  })
  const [pending, setPending] = useState(pendingCount)
  const [offline, setOffline] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [missing, setMissing] = useState(false)

  // Serverovy stav bez fronty. Obrazovka = base + fronta.
  const baseRef = useRef<TripWithItems | null>(loadCachedTrip(tripId))
  const rerun = useRef(false)
  const live = useRef(true)

  const render = useCallback(() => {
    const base = baseRef.current
    setTrip(base ? withOutbox(base, userId) : null)
    setPending(pendingCount())
  }, [userId, pendingCount])

  const setBase = useCallback(
    (next: TripWithItems) => {
      baseRef.current = next
      saveCachedTrip(next)
      render()
    },
    [render],
  )

  const flush = useCallback(async () => {
    if (!tripId) return
    if (flushing.has(tripId)) {
      rerun.current = true
      return
    }
    flushing.add(tripId)
    try {
      const hadWork = pendingCount() > 0
      setBase(await sendTrip(tripId))
      if (hadWork && pendingCount() > 0) rerun.current = true
      if (live.current) {
        setOffline(false)
        setError(null)
      }
    } catch (err) {
      if (!live.current) return
      if (err instanceof RejectedBatch) setError(err.message)
      else if (err instanceof HttpError && err.isOffline) setOffline(true)
      else if (err instanceof HttpError && err.status === 404) {
        forgetTrip(tripId)
        baseRef.current = null
        setMissing(true)
      } else setError(errorMessage(err))
    } finally {
      flushing.delete(tripId)
      if (live.current) render()
      if (rerun.current && live.current) {
        rerun.current = false
        void flush()
      }
    }
  }, [tripId, render, setBase, pendingCount])

  useEffect(() => {
    live.current = true
    void flush()
    const timer = setInterval(() => void flush(), POLL_MS)
    const onOnline = () => void flush()
    const onVisible = () => {
      if (document.visibilityState === 'visible') void flush()
    }
    const onOutbox = (event: Event) => {
      if ((event as CustomEvent<string>).detail === tripId) render()
    }
    window.addEventListener('online', onOnline)
    window.addEventListener(OUTBOX_EVENT, onOutbox)
    document.addEventListener('visibilitychange', onVisible)
    return () => {
      live.current = false
      clearInterval(timer)
      window.removeEventListener('online', onOnline)
      window.removeEventListener(OUTBOX_EVENT, onOutbox)
      document.removeEventListener('visibilitychange', onVisible)
    }
  }, [flush, render, tripId])

  const enqueue = useCallback(
    (changes: SyncChangeData[]) => {
      if (changes.length === 0) return
      // Parse doplni vychozi hodnoty a odhali nesmysl hned, ne az na serveru.
      const parsed = changes.map((c) => SyncChangeSchema.parse(c))
      saveQueue(tripId, [...loadQueue(tripId), ...parsed])
      render()
      void flush()
    },
    [tripId, render, flush],
  )

  const change = useCallback(
    (itemId: string, patch: TripItemPatch) =>
      enqueue([{ op: 'patch', itemId, patch, changedAt: new Date().toISOString() }]),
    [enqueue],
  )

  const add = useCallback(
    (items: Omit<TripItemInputData, 'id'>[]) => {
      const changedAt = new Date().toISOString()
      enqueue(items.map((item) => ({ op: 'add', item: { ...item, id: newId() }, changedAt })))
    },
    [enqueue],
  )

  const remove = useCallback(
    (itemId: string) => enqueue([{ op: 'remove', itemId, changedAt: new Date().toISOString() }]),
    [enqueue],
  )

  const setStatus = useCallback(
    (status: TripStatus) => {
      const base = baseRef.current
      // Stejny stav, jaky uz server ma, neni potreba posilat.
      saveQueuedStatus(tripId, base?.status === status ? null : status)
      if (base) patchTripList(base.groupId, tripId, status)
      render()
      void flush()
    },
    [tripId, render, flush],
  )

  const updateTrip = useCallback(
    async (patch: TripPatch) => {
      const updated = await api.trips.update(tripId, patch)
      const base = baseRef.current
      if (base) setBase({ ...base, ...updated })
    },
    [tripId, setBase],
  )

  const deleteTrip = useCallback(async () => {
    await api.trips.remove(tripId)
    forgetTrip(tripId)
    baseRef.current = null
  }, [tripId])

  return {
    trip,
    pending,
    offline,
    error,
    missing,
    enqueue,
    change,
    add,
    remove,
    setStatus,
    updateTrip,
    deleteTrip,
    flush,
  }
}
